import "./polyfills/safari-15";
import * as Sentry from "@sentry/tanstackstart-react";
import { createRouter as createTanStackRouter, isRedirect } from "@tanstack/react-router";
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { setupRouterSsrQueryIntegration } from "@tanstack/react-router-ssr-query";
import { getPublicEnv } from "#/modules/util.instrumentation/public-env";
import { initVersionCheck } from "#/modules/util.instrumentation/version-check";
import { op, syncOpenPanelUser } from "#/modules/util.instrumentation/op";
import { SENTRY_TUNNEL_PATH, syncSentryScope } from "#/modules/util.instrumentation/sentry";
import { initClientDeviceContext } from "#/modules/util.instrumentation/client-device";
import { registerClientRouter } from "#/modules/util.fetch-client/client-router";
import { expectedErrorKind, logExpectedError } from "#/modules/util.errors/expected";
import { clearRouteContextCache } from "./routes/__root";
import { NotFoundComponent } from "#/components/not-found";
import { routeTree } from "./routeTree.gen";

/** Errors browser extensions throw into our frames: noise, never actionable (mirrors apps/portal). */
function isBrowserExtensionError(cause: unknown): boolean {
  return (
    cause instanceof Error &&
    !!cause.stack &&
    /chrome-extension:|moz-extension:|extensions|anonymous scripts/.test(cause.stack)
  );
}

export function getRouter() {
  // Per-router (= per-request on the server) QueryClient: a module-level singleton would be shared
  // across every request and every tenant. Handed to routes via router context so loaders can
  // `ensureQueryData` into the same cache the components read with `useQuery`.
  const queryClient = new QueryClient({
    queryCache: new QueryCache({ onError: followRedirect }),
    mutationCache: new MutationCache({ onError: followRedirect }),
  });

  const router = createTanStackRouter({
    routeTree,
    scrollRestoration: true,
    defaultPreload: "intent",
    defaultNotFoundComponent: NotFoundComponent,
    // Placeholder — the root route's beforeLoad populates the full auth context per request.
    // `environment` defaults to the safe (production) value so dev-only UI can never flash.
    context: {
      queryClient,
      user: null,
      scholen: [],
      isAdmin: false,
      requires2FA: false,
      activeLessen: [],
      lockedLes: null,
      lastSelectedSchoolSlug: null,
      hasReadLatestReleaseNotes: true,
      hasSeenOnboarding: true,
      unreadTicketCount: 0,
      systemNotification: null,
      isImpersonating: false,
      hideImpersonationBanner: false,
      environment: {
        environment: "production",
        isDevelopment: false,
        isTest: false,
        isPerformance: false,
        isAcceptance: false,
        isProduction: true,
      },
    },
  });

  function followRedirect(error: Error) {
    if (router.isServer || !isRedirect(error)) return;
    void router.navigate({ ...error.options, replace: true, ignoreBlocker: true });
  }

  // Dehydrates the query cache after the server loaders run and rehydrates it on the client, so a
  // query seeded by a loader keeps its REAL `dataUpdatedAt` instead of a hand-stamped mount time.
  // Also installs the `QueryClientProvider` via `router.options.Wrap` — no provider in __root.
  setupRouterSsrQueryIntegration({ router, queryClient });

  // Browser SDK. The server half is initialized in instrument.server.mjs (loaded via --import), so
  // this must stay client-only; the router instance is passed to the tracing integration to turn
  // navigations into spans. Config comes from `window.clientEnv` (set by the root shell) rather than
  // from build-time env: one image is deployed to every environment.
  if (!router.isServer) {
    registerClientRouter(router);
    const env = getPublicEnv();

    // Sentry SDK pointed at self-hosted GlitchTip (Sentry-protocol compatible). Replay and the
    // feedback widget are gone with sentry.io — GlitchTip cannot ingest those envelope types.
    Sentry.init({
      enabled: env.SENTRY_ENABLED,
      dsn: env.GLITCHTIP_DSN,
      release: env.APP_VERSION,
      environment: env.ASPNETCORE_ENVIRONMENT,
      // First-party tunnel route (routes/api/monitoring.tsx) so ad-blockers can't drop events; it
      // forwards each envelope to the host of the DSN in its header.
      tunnel: SENTRY_TUNNEL_PATH,
      integrations: [
        Sentry.tanstackRouterBrowserTracingIntegration(router),
        // Warnings and errors written to the console become GlitchTip log lines; info stays local.
        Sentry.consoleLoggingIntegration({ levels: ["warn", "error"] }),
      ],
      enableLogs: true,
      tracesSampleRate: 1.0,
      ignoreErrors: ["AbortError"],
      beforeSend(event, hint) {
        // Google's page-translation service throws into our frames; not our bug.
        if (event.request?.url?.includes("translate-pa.googleapis.com")) return null;
        if (isBrowserExtensionError(hint.originalException)) return null;
        const kind = expectedErrorKind(hint.originalException);
        if (!kind) return event;
        logExpectedError(kind, hint.originalException);
        return null;
      },
    });
  }

  // Observation only. A lazy chunk that fails to import (tab on a previous build after a
  // deploy, or a device that cannot load the chunk at all) is handled by TanStack Router itself:
  // `lazyRouteComponent` reloads once on a module-not-found error and otherwise hands the error
  // to the error boundary, which shows the "new version" text. Calling `preventDefault()` here
  // would make Vite resolve the import with `undefined` instead of throwing, so the route
  // component would crash on `res["default"]` before a reload can win the race (seen on WebKit
  // only). A log line rather than an issue: a stale tab is expected after every deploy.
  if (!router.isServer) {
    window.addEventListener("vite:preloadError", (event) => {
      Sentry.logger.warn("stale chunk: lazy import failed", {
        reason: event.payload.message,
        "display.standalone": String(window.matchMedia("(display-mode: standalone)").matches),
        "url.full": window.location.href,
        "user_agent.original": navigator.userAgent,
      });
    });

    // Proactive half: detect a new server build and upgrade on the next navigation.
    initVersionCheck(router);
  }

  // `onBeforeLoad` fires on real loads (navigation, redirect, invalidate) but not on preloads —
  // exactly when the root's cached auth context must be re-fetched.
  if (!router.isServer) {
    router.subscribe("onBeforeLoad", () => {
      clearRouteContextCache();
    });
  }

  // OpenPanel product analytics — module singleton, disabled outside
  // production. The subscription (re-)identifies the session user after every resolved load so
  // login/logout flips are picked up. Impersonated sessions identify as the impersonated user with
  // `impersonated: true` on every event, so profile drill-downs stay meaningful while support/admin
  // activity remains filterable in reports. The active school (from the $school URL param, matched
  // against the user's own scholen) is tagged as an OpenPanel group for per-school analytics.
  if (!router.isServer) {
    op.init();
    void initClientDeviceContext()
      .catch(Sentry.captureException)
      .then(() => {
        op.trackScreenViews();
        op.screenView();
      });
    router.subscribe("onResolved", () => {
      const context = router.state.matches[0]?.context;
      if (!context) return;
      const params = router.state.matches.at(-1)?.params;
      const schoolSlug = params && "school" in params ? params.school : undefined;
      const school = context.scholen.find((s) => s.slug === schoolSlug) ?? null;
      syncSentryScope(context.user, school, { impersonated: context.isImpersonating });
      syncOpenPanelUser(context.user, school, { impersonated: context.isImpersonating });
    });
  }

  return router;
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }

  // Lets a child route opt out of the school `DefaultLayout` chrome via `staticData: { layout: false }`.
  interface StaticDataRouteOption {
    layout?: boolean;
  }
}
